import { useMemo, useState } from 'react';
import type { WikiPageNode } from '../types';

interface Props {
  root: WikiPageNode;
  selectedPath: string;
  onSelect: (path: string) => void;
  onNewPage: (parentPath: string) => void;
}

function label(path: string) {
  const seg = path.split('/').filter(Boolean).pop() ?? '';
  // ADO stores "-" for spaces in git paths but the pages API returns display paths; unescape just in case.
  return seg.replace(/%2D/g, '-');
}

function sortNodes(nodes: WikiPageNode[] | undefined): WikiPageNode[] {
  return [...(nodes ?? [])].sort((a, b) => (a.order ?? 0) - (b.order ?? 0) || a.path.localeCompare(b.path));
}

function matches(node: WikiPageNode, q: string): boolean {
  if (!q) return true;
  if (label(node.path).toLowerCase().includes(q)) return true;
  return (node.subPages ?? []).some(c => matches(c, q));
}

function TreeNode({ node, depth, selectedPath, onSelect, onNewPage, expanded, toggle, q }: {
  node: WikiPageNode;
  depth: number;
  selectedPath: string;
  onSelect: (p: string) => void;
  onNewPage: (p: string) => void;
  expanded: Set<string>;
  toggle: (p: string) => void;
  q: string;
}) {
  if (!matches(node, q)) return null;
  const children = sortNodes(node.subPages);
  const hasChildren = children.length > 0;
  const isOpen = q ? true : expanded.has(node.path) || selectedPath.startsWith(`${node.path}/`);
  const isSelected = node.path === selectedPath;

  return (
    <li>
      <div className={`tree-row${isSelected ? ' is-selected' : ''}`} style={{ paddingLeft: 8 + depth * 14 }}>
        <button className={`tree-twisty${hasChildren ? '' : ' is-leaf'}`} onClick={() => hasChildren && toggle(node.path)} aria-label={isOpen ? 'Collapse' : 'Expand'} tabIndex={-1}>
          {hasChildren ? (isOpen ? '▾' : '▸') : ''}
        </button>
        <button className="tree-label" onClick={() => onSelect(node.path)} title={node.path}>
          {label(node.path)}
        </button>
        <button className="tree-add" title="New sub-page" onClick={() => onNewPage(node.path)} aria-label="New sub-page">
          +
        </button>
      </div>
      {hasChildren && isOpen && (
        <ul>
          {children.map(c => (
            <TreeNode key={c.path} node={c} depth={depth + 1} selectedPath={selectedPath} onSelect={onSelect} onNewPage={onNewPage} expanded={expanded} toggle={toggle} q={q} />
          ))}
        </ul>
      )}
    </li>
  );
}

export default function PageTree({ root, selectedPath, onSelect, onNewPage }: Props) {
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [filter, setFilter] = useState('');
  const q = filter.trim().toLowerCase();
  const top = useMemo(() => sortNodes(root.subPages), [root]);

  const toggle = (p: string) =>
    setExpanded(prev => {
      const next = new Set(prev);
      if (next.has(p)) next.delete(p);
      else next.add(p);
      return next;
    });

  return (
    <nav className="page-tree">
      <div className="page-tree-head">
        <input className="input input-sm" placeholder="Filter pages…" value={filter} onChange={e => setFilter(e.target.value)} />
        <button className="btn btn-sm" onClick={() => onNewPage('/')} title="New top-level page">
          + Page
        </button>
      </div>
      <ul>
        {top.map(n => (
          <TreeNode key={n.path} node={n} depth={0} selectedPath={selectedPath} onSelect={onSelect} onNewPage={onNewPage} expanded={expanded} toggle={toggle} q={q} />
        ))}
      </ul>
      {top.length === 0 && <div className="muted" style={{ padding: 12 }}>This wiki has no pages yet.</div>}
    </nav>
  );
}
